import { createFileRoute } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  secretApi, integrationApi, toolApi,
  type TenantSecret, type Integration, type ToolDefinition,
} from '@/lib/api';
import { Store, Check, X, Settings2, Power, Zap } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PageGuide } from '@/components/PageGuide';
import styles from './_auth.settings.tools-catalog.module.css';

export const Route = createFileRoute('/_auth/settings/tools-catalog')({
  component: ToolsCatalogPage,
});

// ═══════════════════════════════════════════════════════════
// Brand SVG logos (inline — no external dependencies)
// ═══════════════════════════════════════════════════════════

function ResendLogo() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M2 6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6Z" fill="#000"/>
      <path d="m2 6 10 7 10-7" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M2 18l7-5.5M22 18l-7-5.5" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  );
}

function TwilioLogo() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="4" fill="#F22F46"/>
      <circle cx="9" cy="9" r="1.5" fill="#fff"/>
      <circle cx="15" cy="9" r="1.5" fill="#fff"/>
      <circle cx="9" cy="15" r="1.5" fill="#fff"/>
      <circle cx="15" cy="15" r="1.5" fill="#fff"/>
    </svg>
  );
}

function StripeLogo() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="4" fill="#635BFF"/>
      <path d="M11.5 8.5c0-.6.5-1 1.1-1 1.2 0 2.4.5 3.4 1.2l1-2.4C15.8 5.4 14.2 5 12.7 5 10.2 5 8.5 6.4 8.5 8.6c0 3.8 5 3.1 5 4.9 0 .6-.5 1-1.3 1-1.3 0-2.9-.7-3.9-1.5l-1.1 2.5C8.6 16.5 10.3 17 12.1 17c2.6 0 4.4-1.3 4.4-3.6 0-4-5-3.3-5-4.9Z" fill="#fff"/>
    </svg>
  );
}

function SlackLogo() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M6.5 14.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Zm1 0v4a1.5 1.5 0 0 0 3 0v-4a1.5 1.5 0 0 0-3 0Z" fill="#E01E5A"/>
      <path d="M9.5 6.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm0 1h-4a1.5 1.5 0 0 0 0 3h4a1.5 1.5 0 0 0 0-3Z" fill="#36C5F0"/>
      <path d="M17.5 9.5a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm-1 0v-4a1.5 1.5 0 0 0-3 0v4a1.5 1.5 0 0 0 3 0Z" fill="#2EB67D"/>
      <path d="M14.5 17.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm0-1h4a1.5 1.5 0 0 0 0-3h-4a1.5 1.5 0 0 0 0 3Z" fill="#ECB22E"/>
    </svg>
  );
}

function GoogleCalendarLogo() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <rect x="3" y="4" width="18" height="18" rx="2" fill="#fff" stroke="#4285F4" strokeWidth="1.5"/>
      <rect x="3" y="4" width="18" height="5" rx="2" fill="#4285F4"/>
      <text x="12" y="16" textAnchor="middle" fontSize="7" fontWeight="700" fill="#4285F4" fontFamily="system-ui">31</text>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════
// Catalog definition
// ═══════════════════════════════════════════════════════════

interface SecretField {
  name: string;
  label: string;
  placeholder: string;
  type?: 'password' | 'text';
}

interface ConfigField {
  key: string;
  label: string;
  placeholder: string;
  defaultValue?: string;
}

interface CatalogTool {
  toolName: string;
  displayName: string;
  description: string;
  httpConfig: { method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; path: string };
  inputSchema: object;
}

interface CatalogEntry {
  id: string;
  name: string;
  description: string;
  logo: ReactNode;
  color: string;
  category: string;
  secrets: SecretField[];
  configFields?: ConfigField[];
  integration: {
    name: string;
    baseUrl: string;
    authType: 'BEARER' | 'API_KEY';
    authSecretRef: string;
    apiKeyHeader?: string;
  };
  tools: CatalogTool[];
}

const CATALOG: CatalogEntry[] = [
  {
    id: 'resend',
    name: 'Resend',
    description: 'Modern transactional email API. Send beautiful HTML emails with delivery tracking and analytics.',
    logo: <ResendLogo />,
    color: '#000000',
    category: 'Email',
    secrets: [
      { name: 'RESEND_API_KEY', label: 'API Key', placeholder: 're_...', type: 'password' },
    ],
    configFields: [
      { key: 'RESEND_DEFAULT_FROM', label: 'Default sender address', placeholder: 'noreply@yourdomain.com', defaultValue: 'onboarding@resend.dev' },
    ],
    integration: {
      name: 'Resend',
      baseUrl: 'https://api.resend.com',
      authType: 'BEARER',
      authSecretRef: '{{secrets.RESEND_API_KEY}}',
    },
    tools: [
      {
        toolName: 'send_email',
        displayName: 'Send Email (Resend)',
        description: 'Send a transactional email via Resend. Provide recipient, subject, and HTML body. Uses the default sender address unless overridden.',
        httpConfig: { method: 'POST', path: '/emails' },
        inputSchema: {
          type: 'object',
          properties: {
            to: { type: 'string', description: 'Recipient email address' },
            subject: { type: 'string', description: 'Email subject line' },
            html: { type: 'string', description: 'HTML body content' },
            from: { type: 'string', description: 'Sender address. Leave empty to use the default configured sender.' },
          },
          required: ['to', 'subject', 'html'],
        },
      },
    ],
  },
  {
    id: 'twilio',
    name: 'Twilio',
    description: 'Programmable SMS & voice API. Send text messages worldwide with delivery status callbacks.',
    logo: <TwilioLogo />,
    color: '#F22F46',
    category: 'SMS',
    secrets: [
      { name: 'TWILIO_ACCOUNT_SID', label: 'Account SID', placeholder: 'AC...', type: 'text' },
      { name: 'TWILIO_AUTH_TOKEN', label: 'Auth Token', placeholder: 'your auth token', type: 'password' },
    ],
    configFields: [
      { key: 'TWILIO_DEFAULT_FROM', label: 'Default "From" number', placeholder: '+15551234567' },
    ],
    integration: {
      name: 'Twilio',
      baseUrl: 'https://api.twilio.com/2010-04-01',
      authType: 'BEARER',
      authSecretRef: '{{secrets.TWILIO_AUTH_TOKEN}}',
    },
    tools: [
      {
        toolName: 'send_sms',
        displayName: 'Send SMS (Twilio)',
        description: 'Send an SMS message via Twilio. Provide recipient phone number and message body. Uses the default sender number unless overridden.',
        httpConfig: { method: 'POST', path: '/Accounts/{{secrets.TWILIO_ACCOUNT_SID}}/Messages.json' },
        inputSchema: {
          type: 'object',
          properties: {
            To: { type: 'string', description: 'Recipient phone number in E.164 format (e.g. +15551234567)' },
            Body: { type: 'string', description: 'SMS message text' },
            From: { type: 'string', description: 'Sender phone number. Leave empty to use default.' },
          },
          required: ['To', 'Body'],
        },
      },
    ],
  },
  {
    id: 'stripe',
    name: 'Stripe',
    description: 'Payment processing platform. Create payment links, manage customers, and handle subscriptions.',
    logo: <StripeLogo />,
    color: '#635BFF',
    category: 'Payments',
    secrets: [
      { name: 'STRIPE_API_KEY', label: 'Secret Key', placeholder: 'sk_live_...', type: 'password' },
    ],
    integration: {
      name: 'Stripe',
      baseUrl: 'https://api.stripe.com/v1',
      authType: 'BEARER',
      authSecretRef: '{{secrets.STRIPE_API_KEY}}',
    },
    tools: [
      {
        toolName: 'create_payment_link',
        displayName: 'Create Payment Link (Stripe)',
        description: 'Create a shareable payment link for a one-time payment. Returns a URL the customer can use to pay.',
        httpConfig: { method: 'POST', path: '/payment_links' },
        inputSchema: {
          type: 'object',
          properties: {
            amount: { type: 'number', description: 'Amount in cents (e.g. 2500 for €25.00)' },
            currency: { type: 'string', description: 'ISO currency code (e.g. eur, usd)' },
            description: { type: 'string', description: 'Product or payment description' },
          },
          required: ['amount', 'currency'],
        },
      },
      {
        toolName: 'list_stripe_customers',
        displayName: 'List Customers (Stripe)',
        description: 'List recent Stripe customers. Returns customer name, email, and metadata.',
        httpConfig: { method: 'GET', path: '/customers' },
        inputSchema: {
          type: 'object',
          properties: {
            limit: { type: 'number', description: 'Max results (default 10, max 100)' },
            email: { type: 'string', description: 'Filter by email address' },
          },
        },
      },
    ],
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Post messages to any Slack channel using a Bot Token. Supports rich formatting, attachments, and threads.',
    logo: <SlackLogo />,
    color: '#4A154B',
    category: 'Messaging',
    secrets: [
      { name: 'SLACK_BOT_TOKEN', label: 'Bot Token', placeholder: 'xoxb-...', type: 'password' },
    ],
    configFields: [
      { key: 'SLACK_DEFAULT_CHANNEL', label: 'Default channel', placeholder: '#general', defaultValue: '#general' },
    ],
    integration: {
      name: 'Slack',
      baseUrl: 'https://slack.com/api',
      authType: 'BEARER',
      authSecretRef: '{{secrets.SLACK_BOT_TOKEN}}',
    },
    tools: [
      {
        toolName: 'send_slack_message',
        displayName: 'Send Slack Message',
        description: 'Post a message to a Slack channel via Bot Token. Specify the channel per message — the agent decides where to send based on context. Supports Slack markdown (mrkdwn) formatting.',
        httpConfig: { method: 'POST', path: '/chat.postMessage' },
        inputSchema: {
          type: 'object',
          properties: {
            channel: { type: 'string', description: 'Channel to post to (e.g. #general, #support, or a channel ID like C01234). Uses the default channel if not specified.' },
            text: { type: 'string', description: 'Message text (supports Slack mrkdwn formatting: *bold*, _italic_, `code`, ```code blocks```)' },
          },
          required: ['text', 'channel'],
        },
      },
    ],
  },
  {
    id: 'google_calendar',
    name: 'Google Calendar',
    description: 'Create and manage calendar events. Schedule meetings, set reminders, and check availability.',
    logo: <GoogleCalendarLogo />,
    color: '#4285F4',
    category: 'Productivity',
    secrets: [
      { name: 'GOOGLE_CALENDAR_API_KEY', label: 'API Key', placeholder: 'AIza...', type: 'password' },
    ],
    configFields: [
      { key: 'GOOGLE_CALENDAR_ID', label: 'Calendar ID', placeholder: 'primary', defaultValue: 'primary' },
    ],
    integration: {
      name: 'Google Calendar',
      baseUrl: 'https://www.googleapis.com/calendar/v3',
      authType: 'API_KEY',
      authSecretRef: '{{secrets.GOOGLE_CALENDAR_API_KEY}}',
      apiKeyHeader: 'x-goog-api-key',
    },
    tools: [
      {
        toolName: 'create_calendar_event',
        displayName: 'Create Event (Google Calendar)',
        description: 'Create a new calendar event with title, start/end time, and optional description.',
        httpConfig: { method: 'POST', path: '/calendars/{{secrets.GOOGLE_CALENDAR_ID}}/events' },
        inputSchema: {
          type: 'object',
          properties: {
            summary: { type: 'string', description: 'Event title' },
            start: { type: 'string', description: 'Start time in ISO 8601 (e.g. 2024-03-15T10:00:00+02:00)' },
            end: { type: 'string', description: 'End time in ISO 8601' },
            description: { type: 'string', description: 'Event description' },
          },
          required: ['summary', 'start', 'end'],
        },
      },
      {
        toolName: 'list_calendar_events',
        displayName: 'List Events (Google Calendar)',
        description: 'List upcoming calendar events. Returns title, time, attendees.',
        httpConfig: { method: 'GET', path: '/calendars/{{secrets.GOOGLE_CALENDAR_ID}}/events' },
        inputSchema: {
          type: 'object',
          properties: {
            maxResults: { type: 'number', description: 'Max events to return (default 10)' },
            timeMin: { type: 'string', description: 'Start of time range (ISO 8601). Defaults to now.' },
          },
        },
      },
    ],
  },
];

// ═══════════════════════════════════════════════════════════
// Activation helpers
// ═══════════════════════════════════════════════════════════

type ActivationStep = 'idle' | 'secrets' | 'integration' | 'tools' | 'done' | 'error';

function isEntryActive(entry: CatalogEntry, secrets: TenantSecret[], integrations: Integration[]): boolean {
  const hasSecrets = entry.secrets.every(s => secrets.some(sec => sec.name === s.name));
  const hasIntegration = integrations.some(i => i.name === entry.integration.name && i.active);
  return hasSecrets && hasIntegration;
}

// ═══════════════════════════════════════════════════════════
// Page component
// ═══════════════════════════════════════════════════════════

export default function ToolsCatalogPage() {
  const qc = useQueryClient();
  const { data: secrets = [] } = useQuery({ queryKey: ['secrets'], queryFn: secretApi.list });
  const { data: integrations = [] } = useQuery({ queryKey: ['integrations'], queryFn: integrationApi.list });
  const { data: tools = [] } = useQuery({ queryKey: ['tools'], queryFn: toolApi.list });

  const [activating, setActivating] = useState<CatalogEntry | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [step, setStep] = useState<ActivationStep>('idle');
  const [error, setError] = useState<string | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<CatalogEntry | null>(null);

  function openActivation(entry: CatalogEntry) {
    const defaults: Record<string, string> = {};
    entry.secrets.forEach(s => { defaults[s.name] = ''; });
    entry.configFields?.forEach(f => { defaults[f.key] = f.defaultValue ?? ''; });
    setFormValues(defaults);
    setActivating(entry);
    setStep('idle');
    setError(null);
  }

  async function handleActivate() {
    if (!activating) return;
    setError(null);

    try {
      // Step 1: Create secrets
      setStep('secrets');
      for (const s of activating.secrets) {
        const value = formValues[s.name];
        if (!value) throw new Error(`${s.label} is required`);
        try {
          await secretApi.create({ name: s.name, value, description: `Auto-created by ${activating.name} activation` });
        } catch (e) {
          // Secret might already exist — try updating
          if ((e as Error).message?.includes('already exists')) {
            await secretApi.update(s.name, { value });
          } else {
            throw e;
          }
        }
      }
      // Save config fields as secrets too
      if (activating.configFields) {
        for (const f of activating.configFields) {
          const value = formValues[f.key];
          if (value) {
            try {
              await secretApi.create({ name: f.key, value, description: `${activating.name} config: ${f.label}` });
            } catch (e) {
              if ((e as Error).message?.includes('already exists')) {
                await secretApi.update(f.key, { value });
              } else {
                throw e;
              }
            }
          }
        }
      }

      // Step 2: Create integration
      setStep('integration');
      const intg = activating.integration;
      await integrationApi.create({
        name: intg.name,
        type: 'REST_API',
        baseUrl: intg.baseUrl,
        authType: intg.authType,
        authValue: intg.authSecretRef,
        apiKeyHeader: intg.apiKeyHeader,
      });

      // Step 3: Create tools
      setStep('tools');
      // Find the integration we just created
      const freshIntegrations = await integrationApi.list();
      const createdIntg = (freshIntegrations as Integration[]).find(i => i.name === intg.name);

      for (const tool of activating.tools) {
        await toolApi.create({
          toolName: tool.toolName,
          displayName: tool.displayName,
          description: tool.description,
          executorType: 'HTTP',
          integrationId: createdIntg?.id,
          httpConfig: tool.httpConfig,
          inputSchema: tool.inputSchema,
        });
      }

      setStep('done');
      qc.invalidateQueries({ queryKey: ['secrets'] });
      qc.invalidateQueries({ queryKey: ['integrations'] });
      qc.invalidateQueries({ queryKey: ['tools'] });

      // Auto-close after brief pause
      setTimeout(() => {
        setActivating(null);
        setStep('idle');
      }, 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Activation failed');
      setStep('error');
    }
  }

  async function handleDeactivate(entry: CatalogEntry) {
    // Archive tools and delete integration — keep secrets
    const matchingTools = (tools as ToolDefinition[]).filter(t =>
      entry.tools.some(et => et.toolName === t.toolName)
    );
    for (const t of matchingTools) {
      try { await toolApi.delete(t.id); } catch { /* ignore */ }
    }
    const matchingIntg = (integrations as Integration[]).find(i => i.name === entry.integration.name);
    if (matchingIntg) {
      try { await integrationApi.delete(matchingIntg.id); } catch { /* ignore */ }
    }
    setDeactivateTarget(null);
    qc.invalidateQueries({ queryKey: ['integrations'] });
    qc.invalidateQueries({ queryKey: ['tools'] });
  }

  const isActivationInProgress = step !== 'idle' && step !== 'error';

  return (
    <div className={styles.page}>
      <PageGuide pageKey="tools-catalog" />

      <div className={styles.header}>
        <div>
          <h1 className={styles.title}><Store size={22} /> Tool Catalog</h1>
          <p className={styles.subtitle}>
            Pre-built integrations ready to activate. API keys are stored securely in the Secret Manager —
            tools are created automatically.
          </p>
        </div>
      </div>

      <div className={styles.grid}>
        {CATALOG.map(entry => {
          const active = isEntryActive(entry, secrets as TenantSecret[], integrations as Integration[]);
          return (
            <div
              key={entry.id}
              className={`${styles.card} ${active ? styles.cardActive : ''}`}
              style={{ '--card-accent': entry.color } as React.CSSProperties}
            >
              {/* Status badge */}
              <div className={`${styles.statusBadge} ${active ? styles.badgeActive : styles.badgeAvailable}`}>
                {active ? <><Check size={10} /> Active</> : 'Available'}
              </div>

              {/* Header: logo + name */}
              <div className={styles.cardHeader}>
                <div className={styles.cardLogo}>{entry.logo}</div>
                <div className={styles.cardInfo}>
                  <div className={styles.cardName}>{entry.name}</div>
                  <div className={styles.cardCategory}>{entry.category}</div>
                </div>
              </div>

              {/* Description */}
              <div className={styles.cardDesc}>{entry.description}</div>

              {/* Tools this will create */}
              <div className={styles.toolList}>
                {entry.tools.map(t => (
                  <span key={t.toolName} className={styles.toolPill}>{t.toolName}</span>
                ))}
              </div>

              {/* Actions */}
              <div className={styles.cardActions}>
                {active ? (
                  <div className={styles.activeActions}>
                    <button className={styles.configureBtn} onClick={() => openActivation(entry)}>
                      <Settings2 size={13} /> Reconfigure
                    </button>
                    <button className={styles.deactivateBtn} onClick={() => setDeactivateTarget(entry)}>
                      <Power size={13} />
                    </button>
                  </div>
                ) : (
                  <button className={styles.activateBtn} onClick={() => openActivation(entry)}>
                    <Zap size={14} /> Activate
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Activation modal ── */}
      {activating && (
        <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget && !isActivationInProgress) { setActivating(null); } }}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <div className={styles.modalLogo}>{activating.logo}</div>
              <div className={styles.modalTitleWrap}>
                <div className={styles.modalTitle}>
                  {isEntryActive(activating, secrets as TenantSecret[], integrations as Integration[])
                    ? `Reconfigure ${activating.name}`
                    : `Activate ${activating.name}`
                  }
                </div>
                <div className={styles.modalSubtitle}>{activating.description}</div>
              </div>
              {!isActivationInProgress && (
                <button className={styles.dismissBtn} onClick={() => setActivating(null)}>
                  <X size={16} />
                </button>
              )}
            </div>

            {step === 'done' ? (
              <div className={styles.modalBody}>
                <div className={styles.steps}>
                  <div className={`${styles.step} ${styles.stepDone}`}><Check size={14} /> Secrets saved</div>
                  <div className={`${styles.step} ${styles.stepDone}`}><Check size={14} /> Integration created</div>
                  <div className={`${styles.step} ${styles.stepDone}`}><Check size={14} /> {activating.tools.length} tool{activating.tools.length !== 1 ? 's' : ''} ready</div>
                </div>
              </div>
            ) : isActivationInProgress ? (
              <div className={styles.modalBody}>
                <div className={styles.steps}>
                  <div className={`${styles.step} ${step === 'secrets' ? styles.stepActive : styles.stepDone}`}>
                    {step === 'secrets' ? <div className={styles.stepSpinner} /> : <Check size={14} />}
                    Saving secrets…
                  </div>
                  <div className={`${styles.step} ${step === 'integration' ? styles.stepActive : step === 'tools' ? styles.stepDone : ''}`}>
                    {step === 'integration' ? <div className={styles.stepSpinner} /> : step === 'tools' ? <Check size={14} /> : null}
                    Creating integration…
                  </div>
                  <div className={`${styles.step} ${step === 'tools' ? styles.stepActive : ''}`}>
                    {step === 'tools' ? <div className={styles.stepSpinner} /> : null}
                    Creating {activating.tools.length} tool{activating.tools.length !== 1 ? 's' : ''}…
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className={styles.modalBody}>
                  {/* Secret fields */}
                  {activating.secrets.map(s => (
                    <div key={s.name} className={styles.field}>
                      <label className={styles.label}>{s.label} <span className={styles.req}>*</span></label>
                      <input
                        className={styles.inputMono}
                        type={s.type ?? 'password'}
                        placeholder={s.placeholder}
                        value={formValues[s.name] ?? ''}
                        onChange={e => setFormValues(v => ({ ...v, [s.name]: e.target.value }))}
                        autoComplete="off"
                      />
                      <span className={styles.fieldHint}>
                        Stored as <code style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6875rem' }}>{s.name}</code> in Secret Manager
                      </span>
                    </div>
                  ))}

                  {/* Config fields */}
                  {activating.configFields?.map(f => (
                    <div key={f.key} className={styles.field}>
                      <label className={styles.label}>{f.label}</label>
                      <input
                        className={styles.input}
                        placeholder={f.placeholder}
                        value={formValues[f.key] ?? ''}
                        onChange={e => setFormValues(v => ({ ...v, [f.key]: e.target.value }))}
                      />
                    </div>
                  ))}

                  {error && <div className={styles.error}>{error}</div>}
                </div>
                <div className={styles.modalFooter}>
                  <button className={styles.cancelBtn} onClick={() => setActivating(null)}>Cancel</button>
                  <button
                    className={styles.confirmBtn}
                    onClick={handleActivate}
                    disabled={activating.secrets.some(s => !formValues[s.name]?.trim())}
                  >
                    <Zap size={13} /> Activate {activating.name}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deactivateTarget}
        onCancel={() => setDeactivateTarget(null)}
        onConfirm={() => deactivateTarget && handleDeactivate(deactivateTarget)}
        title={`Deactivate ${deactivateTarget?.name}?`}
        description={`This will remove the integration and archive ${deactivateTarget?.tools.length} tool(s). Your secrets will be kept in the Secret Manager.`}
        confirmLabel="Deactivate"
        variant="danger"
      />
    </div>
  );
}
